import {
  hotkeyText,
  Kbd,
  type KbdHotkey,
} from "@/shared/foundation/primitives";

import { useTimelineContext } from "./TimelineContext";
import styles from "./TimelineFooter.module.css";

interface TimelineFooterProps {
  /** Every matching item, loaded or not. */
  totalCount: number;
  /** Whether the page shortcuts are registered, and so worth hinting. */
  hotkeys: boolean;
}

interface Hint {
  /**
   * Spelled as `useTimelineHotkeys` registers them, so the rendered label
   * cannot drift from the binding. Strings only: joined, they are the hint's React key.
   */
  keys: readonly (KbdHotkey & string)[];
  /** A pointer gesture done with the keys held, shown after them ("scroll"). */
  gesture?: string;
  /** Visible label for what the keys do. */
  action: string;
}

const HINTS: readonly Hint[] = [
  { keys: ["ArrowLeft", "ArrowRight"], action: "previous / next" },
  { keys: ["Shift+ArrowLeft", "Shift+ArrowRight"], action: "pan" },
  // The hook binds "=" with and without Shift ("+" on most layouts); the
  // hotkey library cannot bind "+" itself, so the hint shows "=".
  { keys: ["=", "-"], action: "zoom" },
  // Ctrl/⌘ + wheel is `useTimelineGestures`' own listener, not a hotkey;
  // "Mod" renders as ⌘ on Apple platforms and Ctrl elsewhere, as it zooms.
  { keys: ["Mod"], gesture: "scroll", action: "zoom" },
  { keys: ["F"], action: "fit all" },
];

/**
 * How a screen reader names a hint's keys (the glyphs are aria-hidden), in
 * the hotkey library's words: "ArrowLeft or ArrowRight", "Cmd + scroll".
 */
function spokenKeys(hint: Hint): string {
  const keys = hint.keys.map(hotkeyText).join(" or ");
  return hint.gesture ? `${keys} + ${hint.gesture}` : keys;
}

/** "N of M in view" under the tracks, with the keyboard shortcuts at the other end. */
export function TimelineFooter({ totalCount, hotkeys }: TimelineFooterProps) {
  const { items } = useTimelineContext();
  return (
    <div className={styles.footer}>
      <p className={styles.count}>
        <strong>{items.data.length}</strong> of {totalCount} sessions in view
      </p>
      {hotkeys && (
        <ul className={styles.hints} aria-label="Keyboard shortcuts">
          {HINTS.map((hint) => (
            <li key={hint.keys.join()} className={styles.hint}>
              <span className={styles.keys} aria-hidden="true">
                {hint.keys.map((key) => (
                  <Kbd key={key} hotkey={key} />
                ))}
                {hint.gesture && <Kbd label={hint.gesture} />}
              </span>
              <span className="sr-only">{spokenKeys(hint)}: </span>
              {hint.action}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
